import React from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { createTmdsClient } from '../services/tmds';
import type { ManagedIdentityUpsertPayload } from '../types/trapi';
import { getErrorMessage } from '../utils/errors';
import type { ManagedIdentityEditorRecord } from '../utils/managedIdentityEditor';
import { GUID_PATTERN } from '../utils/ownership';
import DetailsFlyout from './DetailsFlyout';

const useStyles = makeStyles({
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

interface EditState {
  id: string;
  apiPath: string;
  name: string;
  owners: string[];
  primaryOwner: string;
  persistedPrimaryOwnerId: string;
  primaryUnresolved: boolean;
  unresolvedCount: number;
  startDate: string;
  endDate: string;
}

export interface ManagedIdentityEditorProps {
  open: boolean;
  mode: 'create' | 'edit';
  client: ReturnType<typeof createTmdsClient>;
  callerEmail?: string;
  identity?: ManagedIdentityEditorRecord | null;
  fixedApiPath?: string;
  apiPathOptions?: string[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

function normalizeOwner(value: string): string {
  return value.trim().toLowerCase();
}

function uniqueOwners(owners: string[]): string[] {
  return Array.from(new Set(owners.map(normalizeOwner).filter(Boolean)));
}

function initialState(
  mode: 'create' | 'edit',
  callerEmail: string,
  fixedApiPath: string,
  identity?: ManagedIdentityEditorRecord | null,
): EditState {
  if (mode === 'edit' && identity) {
    const owners = identity.owners.length > 0 ? identity.owners : (callerEmail ? [callerEmail] : []);
    const persistedPrimaryOwnerId = normalizeOwner(identity.primaryOwnerId);
    const primaryOwner = identity.primaryUnresolved
      ? ''
      : owners.length === 1
        ? normalizeOwner(owners[0])
        : (owners.some(owner => normalizeOwner(owner) === persistedPrimaryOwnerId) ? persistedPrimaryOwnerId : '');
    return {
      id: identity.id,
      apiPath: fixedApiPath || identity.apiPath,
      name: identity.name,
      owners,
      primaryOwner,
      persistedPrimaryOwnerId,
      primaryUnresolved: identity.primaryUnresolved,
      unresolvedCount: identity.unresolvedCount,
      startDate: identity.startDate,
      endDate: identity.endDate,
    };
  }

  return {
    id: '',
    apiPath: fixedApiPath,
    name: '',
    owners: callerEmail ? [callerEmail] : [],
    primaryOwner: callerEmail,
    persistedPrimaryOwnerId: '',
    primaryUnresolved: false,
    unresolvedCount: 0,
    startDate: '',
    endDate: '',
  };
}

export default function ManagedIdentityEditor({
  open,
  mode,
  client,
  callerEmail = '',
  identity,
  fixedApiPath = '',
  apiPathOptions = [],
  onClose,
  onSaved,
}: ManagedIdentityEditorProps) {
  const styles = useStyles();
  const normalizedCaller = normalizeOwner(callerEmail);
  const [editState, setEditState] = React.useState<EditState | null>(null);
  const [idValidating, setIdValidating] = React.useState(false);
  const [idError, setIdError] = React.useState<string | null>(null);
  const [dirtyOwners, setDirtyOwners] = React.useState<Set<number>>(new Set());
  const [touchedOwners, setTouchedOwners] = React.useState<Set<number>>(new Set());
  const [errorDialog, setErrorDialog] = React.useState({ open: false, title: '', message: '' });
  const [infoDialog, setInfoDialog] = React.useState({ open: false, title: '', message: '' });
  const pendingFocusRef = React.useRef<HTMLInputElement | null>(null);
  const idValidationSeqRef = React.useRef(0);
  const latestIdRef = React.useRef('');
  const isCreate = mode === 'create';

  React.useEffect(() => {
    idValidationSeqRef.current += 1;
    if (!open) {
      latestIdRef.current = '';
      setEditState(null);
      setIdValidating(false);
      setIdError(null);
      setDirtyOwners(new Set());
      setTouchedOwners(new Set());
      return;
    }
    const nextState = initialState(mode, normalizedCaller, fixedApiPath, identity);
    latestIdRef.current = nextState.id;
    setEditState(nextState);
    setIdValidating(false);
    setIdError(null);
    setDirtyOwners(new Set());
    setTouchedOwners(new Set());
  }, [open, mode, identity, normalizedCaller, fixedApiPath]);

  const validateId = React.useCallback(async (idRaw: string) => {
    const id = idRaw.trim();
    if (!id) {
      setIdError('Managed Identity objectId is required.');
      return;
    }
    if (!GUID_PATTERN.test(id)) {
      setIdError('Managed Identity objectId must be a GUID.');
      return;
    }
    const validationSeq = ++idValidationSeqRef.current;
    const validatedId = id.toLowerCase();
    setIdValidating(true);
    setIdError(null);
    try {
      const result = await client.idCheck(id);
      if (
        validationSeq !== idValidationSeqRef.current
        || latestIdRef.current.trim().toLowerCase() !== validatedId
      ) {
        return;
      }
      if (result.status !== 200) {
        setIdError('Unable to validate the objectId.');
        setErrorDialog({
          open: true,
          title: 'ID validation failed',
          message: `Error code ${result.status}${result.error ? `: ${result.error}` : ''}`,
        });
        return;
      }
      const data = result.data!;
      if (data.objectId && data.objectId.toLowerCase() !== validatedId) {
        latestIdRef.current = data.objectId;
        setEditState(previous => previous ? { ...previous, id: data.objectId } : previous);
        setInfoDialog({
          open: true,
          title: 'Resolved Object ID',
          message: 'ClientID provided; related objectId was found and applied.',
        });
      }
      if (data.type !== 'ManagedIdentity') {
        setIdError('Only Managed Identity objectIds are supported.');
        setErrorDialog({
          open: true,
          title: 'Unsupported identity type',
          message: `Type "${data.type}" is not supported.`,
        });
        return;
      }
      setEditState(previous => previous ? { ...previous, name: data.name } : previous);
    } catch (error) {
      if (
        validationSeq !== idValidationSeqRef.current
        || latestIdRef.current.trim().toLowerCase() !== validatedId
      ) {
        return;
      }
      const message = getErrorMessage(error) || 'Unknown validation error.';
      setIdError(message);
      setErrorDialog({ open: true, title: 'Validation error', message });
    } finally {
      if (validationSeq === idValidationSeqRef.current) {
        setIdValidating(false);
      }
    }
  }, [client]);

  const upsert = useMutation({
    mutationFn: async (state: EditState) => {
      const owners = uniqueOwners(state.owners);
      if (normalizedCaller && isCreate) owners.push(normalizedCaller);
      const normalizedOwners = Array.from(new Set(owners));
      const explicitPrimary = normalizeOwner(state.primaryOwner);
      const persistedPrimaryOwnerId = normalizeOwner(state.persistedPrimaryOwnerId);
      const selectedPrimary = explicitPrimary
        || (normalizedOwners.includes(persistedPrimaryOwnerId) ? persistedPrimaryOwnerId : '')
        || (normalizedOwners.length === 1 ? normalizedOwners[0] : '');
      const payload: ManagedIdentityUpsertPayload = {
        objectId: state.id,
        name: state.name,
        startDate: state.startDate || undefined,
        endDate: state.endDate || undefined,
        owners: normalizedOwners,
        isUpdate: !isCreate,
      };

      if (!(state.primaryUnresolved && !explicitPrimary)) {
        payload.primaryOwnerId = selectedPrimary;
      }
      return client.upsertMSI({ apiPath: state.apiPath, payload });
    },
    onSuccess: async () => {
      await onSaved();
      onClose();
    },
    onError: (error: unknown) => {
      console.error('Upsert identity failed:', getErrorMessage(error));
    },
  });

  const requestClose = () => {
    if (!upsert.isPending) onClose();
  };

  const updateOwners = (owners: string[], previous: EditState): EditState => {
    const normalizedOwners = uniqueOwners(owners);
    const currentPrimary = normalizeOwner(previous.primaryOwner);
    const nextPrimary = currentPrimary && normalizedOwners.includes(currentPrimary)
      ? currentPrimary
      : previous.primaryUnresolved
        ? ''
        : normalizedOwners.length === 1
          ? normalizedOwners[0]
          : '';
    return { ...previous, owners, primaryOwner: nextPrimary };
  };

  const updateOwner = (index: number, value: string) => {
    setEditState(previous => {
      if (!previous) return previous;
      const owners = [...previous.owners];
      owners[index] = value;
      return updateOwners(owners, previous);
    });
  };

  const addOwner = () => {
    setEditState(previous => previous ? { ...previous, owners: [...previous.owners, ''] } : previous);
    setTimeout(() => pendingFocusRef.current?.querySelector('input')?.focus(), 150);
  };

  const removeOwner = (index: number) => {
    setEditState(previous => {
      if (!previous || normalizeOwner(previous.owners[index] ?? '') === normalizedCaller) return previous;
      const owners = [...previous.owners];
      owners.splice(index, 1);
      return updateOwners(owners, previous);
    });
    const shiftIndexes = (previous: Set<number>) => {
      const next = new Set<number>();
      previous.forEach(ownerIndex => {
        if (ownerIndex < index) next.add(ownerIndex);
        else if (ownerIndex > index) next.add(ownerIndex - 1);
      });
      return next;
    };
    setDirtyOwners(shiftIndexes);
    setTouchedOwners(shiftIndexes);
  };

  const ownerValidation = React.useMemo(() => {
    if (!editState) {
      return {
        duplicate: false,
        empty: false,
        showEmpty: false,
        primaryMissing: false,
        primaryInvalid: false,
        unresolvedPrimaryNeedsSelection: false,
      };
    }
    const nonEmptyOwners = editState.owners.map(normalizeOwner).filter(Boolean);
    const owners = Array.from(new Set(nonEmptyOwners));
    const duplicate = owners.length !== nonEmptyOwners.length;
    const empty = editState.owners.some(owner => !owner.trim());
    const showEmpty = editState.owners.some((owner, index) =>
      dirtyOwners.has(index) && touchedOwners.has(index) && !owner.trim());
    const explicitPrimary = normalizeOwner(editState.primaryOwner);
    const persistedPrimaryOwnerId = normalizeOwner(editState.persistedPrimaryOwnerId);
    const activePrimary = explicitPrimary
      || (owners.includes(persistedPrimaryOwnerId) ? persistedPrimaryOwnerId : '');
    const unresolvedPrimaryNeedsSelection = editState.primaryUnresolved && !explicitPrimary;
    const primaryMissing = owners.length > 1 && !activePrimary && !editState.primaryUnresolved;
    const primaryInvalid = !!activePrimary && !owners.includes(activePrimary);
    return {
      duplicate,
      empty,
      showEmpty,
      primaryMissing,
      primaryInvalid,
      unresolvedPrimaryNeedsSelection,
    };
  }, [editState, dirtyOwners, touchedOwners]);

  const normalizedOwners = uniqueOwners(editState?.owners ?? []);
  const singleOwner = normalizedOwners.length === 1 ? normalizedOwners[0] : '';
  const trimmedId = editState?.id.trim() ?? '';
  const saveDisabled = !editState
    || !GUID_PATTERN.test(trimmedId)
    || !editState.name.trim()
    || !editState.apiPath
    || normalizedOwners.length === 0
    || ownerValidation.duplicate
    || ownerValidation.empty
    || ownerValidation.primaryMissing
    || ownerValidation.primaryInvalid
    || ownerValidation.unresolvedPrimaryNeedsSelection
    || !!idError
    || idValidating
    || upsert.isPending;

  const formIssues = React.useMemo(() => {
    if (!editState) return [];
    const issues: string[] = [];
    if (!editState.id.trim()) issues.push('Object ID required');
    else if (!GUID_PATTERN.test(editState.id.trim())) issues.push('Invalid GUID');
    if (idError) issues.push(idError);
    if (!editState.name.trim()) issues.push('Display Name required');
    if (!editState.apiPath) issues.push('API Path required');
    if (ownerValidation.empty) issues.push('Owner entries cannot be blank');
    if (ownerValidation.duplicate) issues.push('Duplicate owners');
    if (normalizedOwners.length === 0) issues.push('At least one owner required');
    if (ownerValidation.primaryMissing) issues.push('Primary owner required when multiple owners are listed');
    if (ownerValidation.primaryInvalid) issues.push('Primary owner must be one of the listed owners');
    if (ownerValidation.unresolvedPrimaryNeedsSelection) {
      issues.push('Select a resolved primary owner before saving this identity');
    }
    return issues;
  }, [editState, idError, normalizedOwners.length, ownerValidation]);

  const handleSave = () => {
    if (!editState || saveDisabled) return;
    upsert.mutate({
      ...editState,
      id: trimmedId,
      name: editState.name.trim(),
      owners: normalizedOwners,
    });
  };

  const handleIdentityBlur = () => {
    const value = editState?.id.trim() ?? '';
    if (!value) {
      setIdError('Managed Identity objectId is required.');
    } else if (!GUID_PATTERN.test(value)) {
      setIdError('Managed Identity objectId must be a GUID.');
    } else {
      void validateId(value);
    }
  };

  return (
    <>
      <DetailsFlyout
        open={open}
        onClose={requestClose}
        title={isCreate ? 'Add Identity' : 'Edit Identity'}
        size="medium"
        footer={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
            <Button appearance="secondary" onClick={requestClose} disabled={upsert.isPending}>Cancel</Button>
            <Button appearance="primary" onClick={handleSave} disabled={saveDisabled}>
              {upsert.isPending ? 'Saving...' : 'Save'}
            </Button>
            {formIssues.length > 0 && (
              <Tooltip content={<div>{formIssues.map((issue, index) => <div key={index}>- {issue}</div>)}</div>} relationship="description">
                <Badge appearance="filled" color="danger" size="small">
                  {formIssues.length} issue{formIssues.length === 1 ? '' : 's'}
                </Badge>
              </Tooltip>
            )}
          </div>
        }
      >
        <div className={styles.formGrid}>
          {upsert.isError && <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>{getErrorMessage(upsert.error)}</Text>}

          <Field label="API Path" required validationMessage={isCreate && !editState?.apiPath ? 'Select an API path' : undefined}>
            {fixedApiPath || !isCreate ? (
              <Input value={editState?.apiPath ?? fixedApiPath} disabled />
            ) : (
              <Dropdown
                value={editState?.apiPath || undefined}
                selectedOptions={editState?.apiPath ? [editState.apiPath] : []}
                onOptionSelect={(_, data) => setEditState(previous =>
                  previous ? { ...previous, apiPath: String(data.optionValue ?? '') } : previous)}
                placeholder="Select API Path"
              >
                {apiPathOptions.map(apiPath => <Option key={apiPath} value={apiPath}>{apiPath}</Option>)}
              </Dropdown>
            )}
          </Field>

          <Field label="Managed Identity Object ID" required validationMessage={idError || undefined} validationState={idError ? 'error' : undefined}>
            <Input
              value={editState?.id ?? ''}
              onChange={(_, data) => {
                idValidationSeqRef.current += 1;
                latestIdRef.current = data.value;
                setIdValidating(false);
                setEditState(previous => previous ? { ...previous, id: data.value } : previous);
                setIdError(null);
              }}
              onBlur={handleIdentityBlur}
              placeholder="Enter objectId (GUID)"
              disabled={!isCreate && !!editState?.id}
            />
            {!idError && <Caption1>Validation runs against TMDS when a valid GUID is entered.</Caption1>}
          </Field>

          <Field label="Display Name">
            <Input
              value={editState?.name ?? ''}
              onChange={(_, data) => setEditState(previous =>
                previous ? { ...previous, name: data.value } : previous)}
              placeholder="Display name"
            />
          </Field>

          <div>
            <Text weight="semibold" style={{ display: 'block', marginBottom: 4 }}>Owners</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {editState?.owners.map((owner, index) => {
                const isCaller = !!normalizedCaller && normalizeOwner(owner) === normalizedCaller;
                const isLast = index === editState.owners.length - 1;
                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Input
                      ref={isLast && !isCaller ? pendingFocusRef : undefined}
                      value={owner}
                      onChange={(_, data) => {
                        updateOwner(index, data.value);
                        setDirtyOwners(previous => new Set(previous).add(index));
                      }}
                      onBlur={() => setTouchedOwners(previous => new Set(previous).add(index))}
                      placeholder="owner@domain.com"
                      disabled={isCaller}
                      style={{ flexGrow: 1 }}
                    />
                    {!isCaller && (
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Dismiss12Regular />}
                        aria-label="Remove owner"
                        disabled={upsert.isPending || (editState?.owners.length ?? 0) <= 1}
                        onClick={() => removeOwner(index)}
                      />
                    )}
                  </div>
                );
              })}
              <Button size="small" appearance="secondary" onClick={addOwner} disabled={upsert.isPending}>Add Owner</Button>
              {(ownerValidation.duplicate || ownerValidation.showEmpty) && (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {ownerValidation.duplicate ? 'Duplicate owners detected.' : 'Owner fields contain empty values.'}
                </Text>
              )}
            </div>
          </div>

          <Field
            label="Primary Owner"
            required={normalizedOwners.length > 1 || !!editState?.primaryUnresolved}
            validationMessage={
              ownerValidation.unresolvedPrimaryNeedsSelection
                ? 'The existing primary owner is unavailable. Select a resolved primary owner before saving.'
                : ownerValidation.primaryMissing
                ? 'Select a primary owner when multiple owners are listed.'
                : ownerValidation.primaryInvalid
                  ? 'Primary owner must match one of the owners.'
                  : undefined
            }
            validationState={
              (
                ownerValidation.unresolvedPrimaryNeedsSelection
                || ownerValidation.primaryMissing
                || ownerValidation.primaryInvalid
              )
                ? 'error'
                : undefined
            }
          >
            {normalizedOwners.length <= 1 && !editState?.primaryUnresolved ? (
              <Input
                value={singleOwner}
                placeholder="Add an owner to select a primary owner"
                disabled
              />
            ) : (
              <Dropdown
                value={editState?.primaryOwner || undefined}
                selectedOptions={editState?.primaryOwner ? [editState.primaryOwner] : []}
                onOptionSelect={(_, data) => setEditState(previous =>
                  previous
                    ? {
                        ...previous,
                        primaryOwner: String(data.optionValue ?? ''),
                        primaryUnresolved: false,
                      }
                    : previous)}
                placeholder={
                  editState?.primaryUnresolved
                    ? 'Existing primary owner unavailable; select a replacement'
                    : 'Select primary owner'
                }
                disabled={normalizedOwners.length === 0}
              >
                {normalizedOwners.map(owner => <Option key={owner} value={owner}>{owner}</Option>)}
              </Dropdown>
            )}
            {singleOwner && !editState?.primaryUnresolved && (
              <Caption1>The only owner is the primary owner automatically.</Caption1>
            )}
            {(editState?.unresolvedCount ?? 0) > 0 && (
              <Caption1>
                Some canonical owners could not be resolved to a display name and are preserved server-side.
                Select a resolved replacement when the existing primary owner is unavailable.
              </Caption1>
            )}
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Start Date">
              <Input type="date" value={editState?.startDate ?? ''} onChange={(_, data) =>
                setEditState(previous => previous ? { ...previous, startDate: data.value } : previous)} />
            </Field>
            <Field label="End Date">
              <Input type="date" value={editState?.endDate ?? ''} onChange={(_, data) =>
                setEditState(previous => previous ? { ...previous, endDate: data.value } : previous)} />
            </Field>
          </div>
        </div>
      </DetailsFlyout>

      <Dialog open={errorDialog.open} onOpenChange={(_, data) => setErrorDialog(previous => ({ ...previous, open: !!data.open }))}>
        <DialogSurface><DialogBody>
          <DialogTitle>{errorDialog.title || 'Error'}</DialogTitle>
          <DialogContent><Text>{errorDialog.message}</Text></DialogContent>
          <DialogActions><Button appearance="primary" onClick={() => setErrorDialog(previous => ({ ...previous, open: false }))}>OK</Button></DialogActions>
        </DialogBody></DialogSurface>
      </Dialog>

      <Dialog open={infoDialog.open} onOpenChange={(_, data) => setInfoDialog(previous => ({ ...previous, open: !!data.open }))}>
        <DialogSurface><DialogBody>
          <DialogTitle>{infoDialog.title || 'Information'}</DialogTitle>
          <DialogContent><Text>{infoDialog.message}</Text></DialogContent>
          <DialogActions><Button appearance="primary" onClick={() => setInfoDialog(previous => ({ ...previous, open: false }))}>OK</Button></DialogActions>
        </DialogBody></DialogSurface>
      </Dialog>
    </>
  );
}
